import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { Frequency } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Frequencies";
import { Route } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Routes";
import { Unit } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Unit";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboboxLookup } from "@/Common/Components/Inputs/ComboboxLookupControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SingleSelectTextBoxControl } from "@/Common/Components/Inputs/SingleSelectTextBoxControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { DateTime } from "@/Common/Helpers/DateTime";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { PatientMedicationAction, PrescriptionMedicationStatus } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { DirectoryProfessionalSearchLockingComponent } from "@/ContactDirectory/Components/DirectoryProfessionalSearchLockingComponent";
import { SearchMedication } from "@/Patient/Components/Medication/SearchMedication";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { MedicalItemMedicationAndPrescriptionDosage } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescriptionDosage";
import { MedicationTerm } from "@/Patient/Models/Medication/Enums/MedicationTerm";
import { MedicationDosageDisplayMode, MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { MedicationResponse } from "@/Patient/Models/Medication/ResponseModels/MedicationResponse";
import { LongTermMedicationIcon } from "./LongTermMedicationIcon";
import { PatientMedicationStateManager } from "./PatientMedicationStateManager";

import styles from "./PatientMedicationView.module.scss";

import img_icon_delete from "@/Globals/_images/icons/delete.png";

interface IPatientMedicationViewProps {
    baseMedication: MedicationModel;
    actions?: Immutable.List<ComboBoxDropDownItem>; // Optional only if BaseMedication Commit Date is null
    defaultSelectedActionCode?: PatientMedicationAction; // Optional only if BaseMedication Commit Date is null
    saveEventDispatchComponentId?: string;
    useDefaultLongTermValueAndDuration?: boolean;

    onStateUpdate?: (state: PatientMedicationViewState) => void;
    onMedicationNameValidated?: (isValid: boolean) => void;
}

interface DateChangeResult {
    date: Date | null;
    datePrecision: LifeStageDatePrecisionEnum | null;
    dateText: string;
}

class PatientMedicationViewState {
    public baseMedicationUpdate: MedicationModel;

    public newMedication: MedicationModel;
    public selectedActionCode: PatientMedicationAction;

    public isSearchingMed: boolean;
    public isSearchingMedEventId: string;

    public isLongTermManuallyChanged: boolean;
    public isDurationManuallyChangedByDosageId: { [id: string]: boolean };
    public isEndDateManuallyChanged: boolean;
    public initialDurationByDosageId: { [id: string]: number };
    public writtenDateText: string;
    public startDateText: string;

    constructor(init?: Partial<PatientMedicationViewState>) {
        Object.assign(this, init);
    }

    public static default(): PatientMedicationViewState {
        return new PatientMedicationViewState({
            baseMedicationUpdate: null,
            newMedication: null,
            selectedActionCode: null,

            isSearchingMed: false,
            isSearchingMedEventId: "",

            isLongTermManuallyChanged: false,
            isDurationManuallyChangedByDosageId: {},
            isEndDateManuallyChanged: false,

            writtenDateText: "",
            startDateText: ""
        });
    }
}

class PatientMedicationView extends BaseReactComponent<IPatientMedicationViewProps, PatientMedicationViewState> {
    private dispatcherReference: string;

    public constructor(props: IPatientMedicationViewProps) {
        super(props);

        this.state = this.buildInitialState(props);
    }

    private buildInitialState(props: IPatientMedicationViewProps): PatientMedicationViewState {
        const baseMedicationUpdate = props.baseMedication.Clone();
        const initialDurationByDosageId: Record<string, number> = {};

        baseMedicationUpdate.Model.Dosages.forEach(dosage => {
            initialDurationByDosageId[dosage.Id] = dosage.Duration;
        });

        const writtenDateText = PatientMedicalItemsHelper.GetLifeStageDateString(
            baseMedicationUpdate.Model.WrittenDate,
            baseMedicationUpdate.Model.WrittenDatePrecision
        );

        const startDateText = PatientMedicalItemsHelper.GetLifeStageDateString(
            baseMedicationUpdate.Model.RefillStartDate,
            baseMedicationUpdate.Model.StartDatePrecision
        );

        return new PatientMedicationViewState({
            ...PatientMedicationViewState.default(),
            baseMedicationUpdate,
            newMedication: null,
            initialDurationByDosageId,
            writtenDateText,
            startDateText
        });
    }

    public componentDidMount(): void {
        this.setSelectedAction(this.props.defaultSelectedActionCode ?? PatientMedicationAction.CompleteAndReplace);

        this.dispatcherReference = MiscDispatcher.register(this.onDispatchEvent.bind(this));
    }

    private getCurrentStateManager(state?: PatientMedicationViewState): PatientMedicationStateManager {
        return new PatientMedicationStateManager(state ?? this.state);
    }

    private renderRow(
        labelKey: string,
        element: React.ReactNode,
        newOptions: {
            isRendered?: boolean,
            key?: string,
            alternateLabel?: React.ReactNode,
            alignLabelCentered?: boolean,
            sideElement?: React.ReactNode
        } = null
    ): JSX.Element {
        let options = {
            isRendered: true,
            key: null,
            alternateLabel: "",
            alignLabelCentered: true,
            sideElement: null,
            ...newOptions
        };
        if (!options.isRendered)
            return null;

        return (
            <tr key={options.key || labelKey}>
                <td
                    style={{
                        verticalAlign: options.alignLabelCentered ? "middle" : "top",
                        textAlign: "right",
                        width: "100px",
                        paddingBottom: "4px",
                        paddingRight: "10px",
                        lineHeight: "18px"
                    }}
                >
                    <span>{labelKey ? cacheStore.GetString(labelKey) : options.alternateLabel}</span>
                </td>
                <td colSpan={2} style={{ maxWidth: "600px", paddingBottom: "4px", paddingLeft: "5px" }}>
                    {element}
                </td>
                <td>{options.sideElement ? options.sideElement : " "}</td>
            </tr>
        );
    }

    public render(): JSX.Element {
        if (this.state.selectedActionCode == null)
            return null;

        const manager = this.getCurrentStateManager();

        return (
            <div className="p-body">
                <table className="medical-problems-edit border" style={{ tableLayout: "auto", maxWidth: "700px" }}>
                    <tbody>
                        {(manager.includeNewMed() || manager.isCease()) && this.state.baseMedicationUpdate.Model.CommitDate != null &&
                            this.renderRow(
                                "",
                                <div>
                                    <span style={{ color: "red", "fontWeight": "bold" }}>{this.state.baseMedicationUpdate.GetName(false)}</span>
                                    <span style={{ marginLeft: "5px" }}>{this.state.baseMedicationUpdate.GetDosageString()}</span>
                                </div>
                            )
                        }
                        {this.renderRow(
                            "ActionLabel",
                            <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                <div style={{ "width": "150px" }}>
                                    <Combobox
                                        id="_actionComboBox"
                                        HasEmptyItem={false}
                                        items={this.props.actions}
                                        selectedCode={this.state.selectedActionCode}
                                        onChange={(actionCode: PatientMedicationAction) => this.setSelectedAction(actionCode)}
                                        dataCy="patientMedicationView-action"
                                    />
                                </div>
                                {(manager.includeNewMed() || manager.isCease())
                                    && (this.state.selectedActionCode !== PatientMedicationAction.MarkAsCancelled)
                                    && this.state.baseMedicationUpdate.Model.CommitDate != null
                                    && (
                                        <div className="flex-container" style={{ marginLeft: "10px", alignItems: "center" }}>
                                            <div className="label" style={{ paddingRight: "12px", width: "auto" }}>
                                                <span>{cacheStore.GetString("DateOf")}</span>
                                            </div>
                                            <div className="editablecell">
                                                <DateSelector
                                                    width={110}
                                                    date={this.state.baseMedicationUpdate.Model.CeaseDate}
                                                    dateFormat="yyyy-MM-dd"
                                                    id="kceaseDate_"
                                                    key="kceaseDate_"
                                                    onChangeCallback={newDate => this.mutateMedicationState(m => m.Model.CeaseDate = newDate, true)}
                                                />
                                            </div>
                                        </div>
                                    )
                                }
                            </div>,
                            { isRendered: this.state.baseMedicationUpdate.Model.CommitDate != null }
                        )}
                        {this.renderRow(
                            "ReasonLabel",
                            <TextAreaResizable
                                id={"_ceaseReason_" + this.state.baseMedicationUpdate.Model.Id}
                                minHeightPx={52}
                                resetTextOnIdChange
                                text={this.state.baseMedicationUpdate.Model.CeaseReason}
                                onChange={value => this.mutateMedicationState(m => m.Model.CeaseReason = value, true)}
                            />,
                            {
                                isRendered: (manager.includeNewMed() || manager.isCeaseWithReason()) && this.state.baseMedicationUpdate.Model.CommitDate != null,
                                alignLabelCentered: false
                            }
                        )}
                        <tr><td style={{ height: "20px" }} /></tr>
                        {!manager.includeNewMed() && this.state.baseMedicationUpdate.Model.CommitDate == null &&
                            this.renderUpdateActionView(this.state.baseMedicationUpdate, true)
                        }
                        {manager.includeNewMed() &&
                            this.renderUpdateActionView(this.state.newMedication, false)
                        }
                    </tbody>
                </table>
            </div>
        );
    }

    public resetDateTexts(): void {
        this.mutateState(s => {
            s.startDateText = "";
            s.writtenDateText = "";
        });
    }

    public setSelectedAction(actionCode: PatientMedicationAction): void {
        const manager = this.getCurrentStateManager();
        this.mutateState(s => {
            s.baseMedicationUpdate = this.props.baseMedication.Clone();
            s.newMedication = null;
            s.selectedActionCode = actionCode;

            // Set new PendingStatus to old medication (If its not committed, just update/add the medication and keep the status)
            if (s.baseMedicationUpdate.Model.CommitDate != null) {
                s.baseMedicationUpdate.Model.PrescriptionTab_PendingStatus = actionCode;
                if (s.selectedActionCode != null && s.selectedActionCode === PatientMedicationAction.CompleteAndReplace)
                    s.baseMedicationUpdate.Model.PrescriptionTab_PendingStatus = PrescriptionMedicationStatus.Completed;

                if (this.props.baseMedication.Model.CeaseDate == null && manager.isCease(actionCode))
                    s.baseMedicationUpdate.Model.CeaseDate = moment(Date.now()).toDate();
            }

            if (manager.isReplace(actionCode)) { // Search for similar med on Augment // Reduce // Substitute
                if (s.baseMedicationUpdate.Model.CommitDate != null) {
                    s.isSearchingMed = true;
                    s.isSearchingMedEventId = GuidHelper.NewGuid();
                }
                else if (s.baseMedicationUpdate.Model.Name == null || s.baseMedicationUpdate.Model.Name == "") {
                    s.isSearchingMed = true;
                }
            }

            if (manager.includeNewMed(actionCode)) {
                s.newMedication = this.props.baseMedication.Clone(true);
                s.newMedication.Model.RefillStartDate = moment(Date.now()).toDate();

                if (actionCode === PatientMedicationAction.MarkAsSuspended) { // Indiqué une date de reprise pour la fin de la suspension
                    s.newMedication.Model.RefillStartDate = moment(Date.now()).add("month", 1).toDate();
                    s.newMedication.Model.PrescriptionStatusInt = 4; // New med shows as suspended also
                }

                s.newMedication.Model.RefillEndDate = null;
                s.newMedication.Model.TotalDurationManual = null;
                s.newMedication.Model.OriginalMedicationId = s.baseMedicationUpdate.Model.Id;
                s.newMedication.Model.IsPast = true;
                s.newMedication.SetModeForDosage(s.newMedication.GetLastDosage().Id, MedicationDosageDisplayMode.EndDate);
            }
            else {
                s.baseMedicationUpdate.SetModeForDosage(s.baseMedicationUpdate.GetLastDosage().Id, MedicationDosageDisplayMode.EndDate);
            }
        });

        const isValid = this.state.baseMedicationUpdate?.Model.Name || this.state.newMedication?.Model.Name;
        this.props.onMedicationNameValidated?.(!!isValid);
    }

    private onDispatchEvent(event: LocalEvent): void {
        if (event.Operation === LocalPatientOperations.MedicationTab.UpdateMedicationPopup_PreSave && event.Get<string>("id") == this.props.saveEventDispatchComponentId)
            this.save();
    }

    public mutateMedicationState(mutation: (model: MedicationModel) => void, isBaseMed: boolean): void {
        this.mutateState(s => mutation(isBaseMed ? s.baseMedicationUpdate : s.newMedication));
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        MiscDispatcher.unregister(this.dispatcherReference);
    }

    public componentDidUpdate(): void {
        this.props.onStateUpdate?.(this.state);
    }

    private renderUpdateActionView(item: MedicationModel, isBaseMedication: boolean): JSX.Element[] {
        const idx = 1;
        const lastDosage = item.GetLastDosage();

        const routes: Immutable.List<ComboBoxDropDownItem> = Immutable.List(cacheStore.RoutesCache.map(x => Route.ToComboboxItem(x)));
        const dosageTypes: Immutable.List<FlatLookupView> = Immutable.List(cacheStore.FindLookupsByTypeCode("DosageType").map(x => x));
        const frequencies: Immutable.List<ComboBoxDropDownItem> = Immutable.List(cacheStore.FrequenciesCache.map(x => Frequency.ToComboboxItem(x)));
        const dosageUnits: Immutable.List<ComboBoxDropDownItem> = Immutable.List(cacheStore.UnitsCache.map(x => Unit.toComboboxItem(x)));

        let estimateEndDate = item.EffectiveEndDate() ? item.EffectiveEndDate() : DateTime.nowDeprecated();
        let totalDurationYears = estimateEndDate.getFullYear() - DateTime.nowDeprecated().getFullYear();
        let totalDurationMonths = estimateEndDate.getMonth() - DateTime.nowDeprecated().getMonth();
        let totalDurationDays = estimateEndDate.getDate() - DateTime.nowDeprecated().getDate();

        if (totalDurationDays < 0) {
            totalDurationMonths -= 1;
            totalDurationDays += 30; // Error here, depands on the month, maximum error = 2... acceptable
        }
        if (totalDurationMonths < 0) {
            totalDurationYears -= 1;
            totalDurationMonths += 12;
        }
        let yearString = (totalDurationYears != 0 ? totalDurationYears + " " + cacheStore.GetString("YearOptionalSLabel") + " " : "");
        let monthString = totalDurationMonths + " " + cacheStore.GetString("MonthsLabel") + " ";
        let dayString = totalDurationDays + " " + cacheStore.GetString("DaysLabel");

        let totalDurationIndicator = null;
        if (item.Model.RefillEndDate == null)
            totalDurationIndicator = "∞ " + cacheStore.GetString("DaysLabel");
        else if (totalDurationYears != 0 || Math.abs(totalDurationMonths) > 1)
            totalDurationIndicator = yearString + monthString + dayString;

        let medicationModelDosages: JSX.Element[] = [];
        if (item.Model.Dosages?.count())
            medicationModelDosages = this.renderDosagesForEditMode(item, idx, dosageTypes, frequencies, dosageUnits, isBaseMedication);

        const numberOfRefill = item.Model.NumberOfRefill == null ? "" : item.Model.NumberOfRefill.toString();

        const IsNarcotic = item.IsNarcotic();

        const numberofDays: number = item.TicksToDays(lastDosage.Duration);
        const slash = (<span style={{ margin: "0 2px" }}>/</span>);

        let startLabel = "StartDateLabel";
        if (cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields) {
            startLabel = "TreatmentStartLabel";
        }
        if (this.state.selectedActionCode === PatientMedicationAction.MarkAsSuspended) {
            startLabel = "RestartDateLabel";
        }

        // Render Medication
        return [
            this.renderRow(
                "MedicationLabel",
                <div className={styles.medicationNameContainer}>
                    <SingleSelectTextBoxControl
                        isSelected={isBaseMedication ? !!this.state.baseMedicationUpdate.Model.Name : !!this.state.newMedication.Model.Name}
                        value={item.GetName()}
                        onRemove={() =>
                            this.mutateState(
                                s => {
                                    s.isSearchingMed = true;
                                    s.isSearchingMedEventId = "";
                                    const stateMedicationToUpdate = isBaseMedication ? s.baseMedicationUpdate : s.newMedication;
                                    stateMedicationToUpdate.ClearIdentificationFields();
                                    stateMedicationToUpdate.Model.Name = null;
                                },
                                () => this.getMedicationNameValidation(isBaseMedication)
                            )
                        }
                        selectedModeStyles={{ height: "auto", padding: 0 }}
                        alternateEditComponent={(
                            <SearchMedication
                                disableIndicator
                                disableFavorites
                                debounce={200}
                                width="100%"
                                isInvalid
                                newValue={isBaseMedication ? this.state.baseMedicationUpdate.Model.Name : this.state.newMedication.Model.Name}
                                newValueEventId={this.state.isSearchingMedEventId}
                                className={styles.medicationSearch}
                                dataCy="addMedicationPopup-searchMedication"
                                onItemSelect={response =>
                                    this.mutateState(
                                        s => {
                                            s.isSearchingMed = false;
                                            const stateMedicationToUpdate = isBaseMedication ? s.baseMedicationUpdate : s.newMedication;

                                            if (!response.isFavorite) {
                                                stateMedicationToUpdate.FromResponseLightUpdate(response.responseItem, true);

                                                if (stateMedicationToUpdate.Model.PrescriptionTab_IsEmptyRx == true)
                                                    this.resolveMedication(response.responseItem, stateMedicationToUpdate.Model.Id, medication => {
                                                        if (!this.props.useDefaultLongTermValueAndDuration || medication.Term !== MedicationTerm.LongTerm)
                                                            return;

                                                        this.mutateState(
                                                            s => {
                                                                const medicationState = isBaseMedication ? s.baseMedicationUpdate : s.newMedication;
                                                                const lastDosage = medicationState.GetLastDosage();

                                                                medicationState.Model.IsLongTerm = true;
                                                                medicationState.SetModeForDosage(lastDosage.Id, MedicationDosageDisplayMode.EndDate);
                                                                lastDosage.Duration = medicationState.DaysToTicks(medication.Duration);
                                                                medicationState.RecalculateTotalDuration();

                                                                medicationState.Model.Dosages?.forEach(
                                                                    dosage => s.initialDurationByDosageId[dosage.Id] ??= dosage.Duration
                                                                );
                                                            }
                                                        );
                                                    });
                                            }
                                        },
                                        () => this.getMedicationNameValidation(isBaseMedication)
                                    )
                                }
                                onBlur={customMedicationName =>
                                    this.mutateState(
                                        s => {
                                            if (customMedicationName)
                                                s.isSearchingMed = false;

                                            const stateMedicationToUpdate = isBaseMedication ? s.baseMedicationUpdate : s.newMedication;
                                            stateMedicationToUpdate.ClearIdentificationFields();
                                            stateMedicationToUpdate.Model.Name = customMedicationName;
                                        },
                                        () => this.getMedicationNameValidation(isBaseMedication)
                                    )
                                }
                            />
                        )}
                    />
                    {cacheStore.ClinicSettings.EnableLongTermMedication && (
                        <div className={styles.topRightCheckboxContainer}>
                            <CheckboxControl
                                id={`long-term_${item.Model.Id}`}
                                checked={item.Model.IsLongTerm}
                                label={this.getString("LongTermLabel")}
                                labelClassName={styles.longTermCheckbox}
                                dataCy="addNewMedication-longTermCheckbox"
                                onChange={(isChecked: boolean) => this.onLongTermChange(isChecked, isBaseMedication)}
                            />
                            <LongTermMedicationIcon />
                        </div>
                    )}
                </div>
            ),
            this.renderRow(
                "RouteLabel",
                <Combobox
                    className="sm"
                    selectedCode={item.Model.Route}
                    items={routes}
                    HasEmptyItem
                    id="_RouteComboBox"
                    onChange={(selectedCode: string) => this.mutateMedicationState(m => m.Model.Route = selectedCode, isBaseMedication)}
                />
            ),
            ...medicationModelDosages,
            this.renderRow(
                "InstructionDLabel",
                <TextAreaResizable
                    verticalResizeOnly
                    id={"_insctruction_" + item.Model.Id}
                    minHeightPx={52}
                    resetTextOnIdChange
                    text={item.Model.Instruction}
                    onChange={value => this.mutateMedicationState(m => m.Model.Instruction = value, isBaseMedication)}
                    dataCy="patientMedicationView-instruction"
                />,
                { alignLabelCentered: false }
            ),
            this.renderRow(
                startLabel,
                <div className="flex-container meds" style={{ "justifyContent": "space-between" }}>
                    <div className="flex-container" style={{ alignItems: "center" }}>
                        <FeatureFlagControl
                            flag={cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields}
                            activeFeature={(
                                <TextboxControlWithDatePicker
                                    id="medicationStartDate"
                                    textValue={this.state.startDateText}
                                    dateValue={item.Model.RefillStartDate}
                                    width={110}
                                    onChange={datePrecision => {
                                        const result = this.onDateChange(datePrecision, this.state.startDateText);

                                        this.mutateMedicationState(
                                            med => {
                                                med.Model.RefillStartDate = result.date;
                                                med.Model.StartDatePrecision = result.datePrecision;
                                                med.RecalculateTotalDuration();
                                                this.onStartDateChange(med, result.date, this.state.isEndDateManuallyChanged, this.getCurrentStateManager(this.state).isCease());
                                            },
                                            isBaseMedication
                                        );

                                        this.mutateState(s => s.startDateText = result.dateText);
                                    }}
                                />
                            )}
                            fallbackFeature={(
                                <DateSelector
                                    width={110}
                                    date={item.Model.RefillStartDate}
                                    dateFormat="yyyy-MM-dd"
                                    id="kstart_"
                                    key="kstart_"
                                    onChangeCallback={newDate => this.mutateState(s => {
                                        const medicationModel = isBaseMedication ? s.baseMedicationUpdate : s.newMedication;
                                        medicationModel.SetStartDate(newDate);
                                        this.onStartDateChange(medicationModel, newDate, s.isEndDateManuallyChanged, this.getCurrentStateManager(s).isCease());
                                    })}
                                    dataCy="patientMedicationView-startDate"
                                />
                            )}
                        />

                        {lastDosage.DisplayMode == MedicationDosageDisplayMode.EndDate &&
                            <div style={{ width: "20px", marginLeft: "4px" }}><span>{cacheStore.GetString("AuLabel")}</span></div>
                        }
                        {lastDosage.DisplayMode == MedicationDosageDisplayMode.EndDate && (
                            <DateSelector
                                width={110}
                                date={this.getCurrentStateManager().isCease() ? item.Model.CeaseDate : item.Model.RefillEndDate}
                                dateFormat="yyyy-MM-dd"
                                id="kend_"
                                onChangeCallback={newDate => this.mutateState(s => {
                                    const medicationModel = isBaseMedication ? s.baseMedicationUpdate : s.newMedication;
                                    medicationModel.SetEndDate(newDate, true);
                                    s.isEndDateManuallyChanged = true;

                                    if (
                                        !cacheStore.ClinicSettings.EnableLongTermMedication
                                        || s.isLongTermManuallyChanged
                                        || !settingsStore.configuration.get("LongTermMedicationDurationInDays")
                                        || medicationModel.Model.IsShortTerm
                                    ) {
                                        return;
                                    }

                                    medicationModel.Model.IsLongTerm = this.props.baseMedication.Model.IsLongTerm; // restore initial value

                                    const start = DateOnly.parseUnlessNull(medicationModel.Model.RefillStartDate)?.toJSDate();
                                    const end = DateOnly.parseUnlessNull(newDate)?.toJSDate();
                                    if (start && end) {
                                        const diff = moment(end).diff(moment(start), "days");
                                        const isSameDurationAsLongTermSetting = diff === settingsStore.configuration.get("LongTermMedicationDurationInDays");
                                        medicationModel.Model.IsLongTerm ||= isSameDurationAsLongTermSetting;
                                    }
                                })}
                            />
                        )}
                    </div>

                    <div className="flex-container meds">
                        {lastDosage.DisplayMode != MedicationDosageDisplayMode.EndDate
                            ? (
                                <HyperLinkControl
                                    text={cacheStore.GetString("EndDateDLabel")}
                                    operation={null}
                                    onChange={() => this.mutateMedicationState(m => m.SetModeForDosage(lastDosage.Id, MedicationDosageDisplayMode.EndDate), isBaseMedication)}
                                />
                            )
                            : <span>{cacheStore.GetString("EndDateDLabel")}</span>
                        }
                        {slash}
                        {!IsNarcotic && (
                            <>
                                {lastDosage.DisplayMode != MedicationDosageDisplayMode.Duration ?
                                    (
                                        <HyperLinkControl
                                            text={cacheStore.GetString("DaysLabel")}
                                            onChange={() => this.mutateMedicationState(m => m.SetModeForDosage(lastDosage.Id, MedicationDosageDisplayMode.Duration), isBaseMedication)}
                                            dataCy="patientMedicationView-daysLink"
                                        />
                                    )
                                    : <span>{cacheStore.GetString("DaysLabel")}</span>
                                }
                                {slash}
                            </>
                        )}
                        {lastDosage.DisplayMode != MedicationDosageDisplayMode.Quantity
                            ? (
                                <HyperLinkControl
                                    text={cacheStore.GetString("QuantityLabel")}
                                    onChange={() => this.mutateMedicationState(m => m.SetModeForDosage(lastDosage.Id, MedicationDosageDisplayMode.Quantity), isBaseMedication)}
                                />
                            )
                            : <span>{cacheStore.GetString("QuantityLabel")}</span>
                        }
                    </div>
                </div>
            ),
            this.renderLastDosageDurationModeRow1(numberofDays, lastDosage, item, isBaseMedication),
            this.renderLastDosageQuantityModeRow1(lastDosage, item, isBaseMedication),
            this.renderLastDosageDurationOrQtyModeRow2(numberOfRefill, totalDurationIndicator, item, isBaseMedication),
            this.renderRow(
                "MedicalSummary_Medication_WrittenDateLabel",
                <TextboxControlWithDatePicker
                    id="medicationWrittenDate"
                    textValue={this.state.writtenDateText}
                    dateValue={item.Model.WrittenDate}
                    width={110}
                    onChange={datePrecision => {
                        const result = this.onDateChange(datePrecision, this.state.writtenDateText);
                        this.mutateMedicationState(
                            med => {
                                med.Model.WrittenDate = result.date;
                                med.Model.WrittenDatePrecision = result.datePrecision;
                            },
                            isBaseMedication
                        );

                        this.mutateState(s => s.writtenDateText = result.dateText);
                    }}
                />,
                {
                    isRendered: !this.state.baseMedicationUpdate.Model.CommitDate && cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields
                }
            ),
            this.renderRow(
                "MedicalSummary_Medication_PrescriberLabel",
                <FeatureFlagControl
                    flag={cacheStore.ClinicSettings.EnableContactDirectory}
                    activeFeature={(
                        <DirectoryProfessionalSearchLockingComponent
                            componentId="medicationPrescriber"
                            onItemSelected={selection => {
                                this.mutateMedicationState(
                                    med => {
                                        med.Model.ExternalPrescriberId = selection.entity.id;
                                        med.Model.ExternalPrescriberDisplayName = selection.getDisplayName();
                                    },
                                    isBaseMedication
                                );
                            }}
                            selectedItems={item.Model.ExternalPrescriberId ? [{ Name: item.Model.ExternalPrescriberDisplayName }] : []}
                            onTextChange={text => this.mutateMedicationState(m => m.Model.ExternalPrescriberDisplayName = text, isBaseMedication)}
                            searchText={item.Model.ExternalPrescriberDisplayName}
                            dataCy="medication-upsert-prescriber"
                            onRemoveItem={() => this.mutateMedicationState(
                                med => {
                                    med.Model.ExternalPrescriberId = null;
                                    med.Model.ExternalPrescriberDisplayName = "";
                                },
                                isBaseMedication
                            )}
                            maxLength={200}
                        />
                    )}
                    fallbackFeature={(
                        <TextboxControl
                            id="medicationPrescriber"
                            value={item.Model.ExternalPrescriberDisplayName}
                            onChange={text => this.mutateMedicationState(m => m.Model.ExternalPrescriberDisplayName = text, isBaseMedication)}
                            maxLength={200}
                        />
                    )}
                />,
                {
                    isRendered: !this.state.baseMedicationUpdate.Model.CommitDate && cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields
                }
            ),
            cacheStore.ClinicSettings.EnableMedicationNotes && this.renderRow(
                "MedicalSummary_Medication_InternalNoteLabel",
                <TextAreaResizable
                    verticalResizeOnly
                    id={"patient_medication_view_note_" + item.Model.Id}
                    dataCy="medication-internal-note"
                    minHeightPx={52}
                    resetTextOnIdChange
                    text={item.Model.InternalNote ?? ""}
                    onChange={value => this.mutateMedicationState(
                        medication => medication.Model.InternalNote = value,
                        isBaseMedication
                    )}
                />,
                { alignLabelCentered: false }
            )
        ];
    }

    private renderLastDosageQuantityModeRow1(LastDosage: MedicalItemMedicationAndPrescriptionDosage, item: MedicationModel, IsBaseMed: boolean): JSX.Element {
        return this.renderRow(
            "QuantityLabel",
            <TextboxControl
                IsSmall
                IsNumeric
                className="form-control input"
                value={LastDosage.DosageServedQuantity ?? ""}
                style={{ width: "70px" }}
                debounceMs={300}
                onChange={(value: string) => {
                    this.mutateMedicationState(m => {
                        m.mutateDosage(LastDosage.Id, d => d.DosageServedQuantity = value);
                        m.RecalculateTotalDuration();
                    }, IsBaseMed);
                }}
            />,
            { isRendered: LastDosage.DisplayMode == MedicationDosageDisplayMode.Quantity }
        );
    }

    private renderLastDosageDurationModeRow1(numberofDays: number, LastDosage: MedicalItemMedicationAndPrescriptionDosage, item: MedicationModel, IsBaseMed: boolean): JSX.Element {
        return this.renderRow(
            "",
            <TextboxControl
                IsSmall
                IsNumeric
                className="form-control input"
                value={numberofDays.toString()}
                style={{ width: "70px" }}
                debounceMs={300}
                onChange={(value: string) => this.onDurationChange(value, LastDosage.Id, IsBaseMed)}
                dataCy="patientMedicationView-durationTextbox"
            />,
            {
                isRendered: LastDosage.DisplayMode == MedicationDosageDisplayMode.Duration,
                alternateLabel: `${cacheStore.GetString("DurationLabel")}(${cacheStore.GetString("DaysLabel").substr(0, 1).toLowerCase()})`
            }
        );
    }

    private renderLastDosageDurationOrQtyModeRow2(numberOfRefill: string, TotalDurationIndicator: string, item: MedicationModel, IsBaseMed: boolean): JSX.Element {
        return this.renderRow(
            "RenewalShortLabel",
            <div>
                <span>
                    <TextboxControl
                        IsSmall
                        IsNumeric
                        className="form-control input"
                        value={numberOfRefill}
                        style={{ width: "70px" }}
                        debounceMs={300}
                        onChange={(value: string) => {
                            this.mutateMedicationState(m => {
                                m.Model.NumberOfRefill = Number(value);
                                m.RecalculateTotalDuration();
                            }, IsBaseMed);
                        }}
                        noAutoCompleteStyle={{ display: "inline-block" }}
                        dataCy="patientMedicationView-renewal"
                    />
                </span>
                {(TotalDurationIndicator != null)
                    && <span style={{ "marginLeft": "10px" }}>{TotalDurationIndicator}</span>
                }
            </div>,
            {
                isRendered: !item.IsNarcotic() && item.GetLastDosage().DisplayMode != MedicationDosageDisplayMode.EndDate,
                key: "Refills" + "_" + item.Model.Id
            }
        );
    }

    private renderDosagesForEditMode(medicationModel: MedicationModel, idx: number, dosageTypes: Immutable.List<FlatLookupView>, frequencies: Immutable.List<ComboBoxDropDownItem>, dosageUnits: Immutable.List<ComboBoxDropDownItem>, isBaseMed: boolean): JSX.Element[] {
        const isNarcotic = medicationModel.IsNarcotic();

        return medicationModel.Model.Dosages
            .map((currentDosage, idy) => {
                const numberofDays: number = medicationModel.TicksToDays(currentDosage.Duration);
                const isFirstDosage = idy == 0;
                const isLastDosage = idy == medicationModel.Model.Dosages.count() - 1;

                // Fix Default Values when null
                const selectedDosageTypeId = currentDosage.DosageTypeLookupId ?? cacheStore.FindLookupByLookupCode("DU.PerDose", "DosageType").LookupID;
                const selectedDosageUnitId = currentDosage.DosageUnit?.toString();

                let renderDosageDurationQtyEndDate = currentDosage.DisplayMode == MedicationDosageDisplayMode.Quantity || isNarcotic
                    ? this.renderDosageQty(currentDosage.DosageServedQuantity, medicationModel.Model.Id, currentDosage.Id, idy, isNarcotic, isBaseMed)
                    : this.renderDosageDuration(numberofDays.toString(), medicationModel.Model.Id, currentDosage.Id, idy, isBaseMed);

                return [
                    this.renderRow(
                        "",
                        null,
                        {
                            isRendered: !isFirstDosage,
                            alternateLabel: <div><span style={{ "fontWeight": "bold" }}>{cacheStore.GetString("FollowedByLabel") + ": "}</span></div>
                        }
                    ),
                    this.renderRow(
                        "DosageLabel",
                        <div className="flex-container">
                            <TextboxControl
                                IsSmall
                                className="form-control input"
                                value={currentDosage.DosageString}
                                id={"_dosestring" + idx + "_" + idy}
                                style={{ width: "70px" }}
                                debounceMs={300}
                                onChange={(value: string) => {
                                    this.mutateMedicationState(m => {
                                        m.mutateDosage(currentDosage.Id, d => d.DosageString = value);
                                        m.RecalculateTotalDuration();
                                    }, isBaseMed);
                                }}
                            />
                            <Combobox
                                className="sm"
                                selectedCode={selectedDosageUnitId}
                                items={dosageUnits}
                                HasEmptyItem
                                style={{ margin: "0 5px" }}
                                id="_unitComboBox"
                                onChange={(selectedCode: string) => {
                                    this.mutateMedicationState(m => {
                                        m.mutateDosage(currentDosage.Id, d => d.DosageUnit = Number(selectedCode));
                                    }, isBaseMed);
                                }}
                            />
                            <ComboboxLookup
                                className="sm"
                                selectedItemLookupId={selectedDosageTypeId}
                                itemsLookup={dosageTypes}
                                id="_doseTypeComboBox"
                                onChange={lookupId => {
                                    this.mutateMedicationState(m => {
                                        m.mutateDosage(currentDosage.Id, d => d.DosageTypeLookupId = lookupId);
                                    }, isBaseMed);
                                }}
                            />
                        </div>,
                        {
                            sideElement: !isFirstDosage && (
                                <div
                                    className="editablecell"
                                    onClick={() => {
                                        this.mutateMedicationState((m: MedicationModel) => {
                                            m.DeleteDosage(currentDosage.Id);
                                        }, isBaseMed);
                                    }}
                                >
                                    <img
                                        style={{
                                            width: "16px",
                                            marginTop: "-5px",
                                            marginLeft: "10px"
                                        }}
                                        alt="delete dosage"
                                        className="file-img"
                                        src={img_icon_delete}
                                        aria-expanded="false"
                                    />
                                </div>
                            )
                        }
                    ),
                    this.renderRow(
                        "FrequencyLabel",
                        <div className="flex-container" style={{ alignItems: "center", justifyContent: "flex-start" }}>
                            <Combobox
                                style={{ width: "50%" }}
                                includeCodeInName={false}
                                className="sm"
                                selectedCode={currentDosage.DosageFrequency}
                                items={frequencies}
                                HasEmptyItem
                                id="_frequencyComboBox"
                                onChange={selectedCode =>
                                    this.mutateMedicationState(
                                        m => {
                                            m.mutateDosage(currentDosage.Id, d => d.DosageFrequency = selectedCode);
                                            m.RecalculateTotalDuration();
                                        },
                                        isBaseMed
                                    )
                                }
                            />
                            <CheckboxControl
                                id={"ifneeded" + idx + "_" + idy}
                                labelStyle={{ marginBottom: "0", marginLeft: "10px" }}
                                checked={currentDosage.IfNeeded}
                                label={cacheStore.GetString("OnlyIfNeededLabel")}
                                onChange={() =>
                                    this.mutateMedicationState(m => m.mutateDosage(currentDosage.Id, d => d.IfNeeded = !d.IfNeeded), isBaseMed)
                                }
                            />
                        </div>
                    ),
                    !isLastDosage /* Last Dosage Duration Controls are displayed at the bottom near Start/End Date for medication updates */ &&
                    renderDosageDurationQtyEndDate
                ];
            })
            .flatten()
            .toArray();
    }

    private renderDosageDuration(numberofDays: string, medicalItemId: string, dosageId: string, idy: number, IsBaseMed: boolean): JSX.Element {
        return this.renderRow(
            "DurationLabel",
            <div className="flex-container" style={{ justifyContent: "space-between", marginBottom: "4px" }}>
                <TextboxControl
                    IsSmall
                    IsNumeric
                    className="form-control input"
                    id={"_durationquantity" + medicalItemId + "_" + dosageId}
                    value={numberofDays}
                    style={{ width: "70px" }}
                    debounceMs={300}
                    onChange={(value: string) => this.onDurationChange(value, dosageId, IsBaseMed)}
                />
                <div className="flex-container meds">
                    <span>{cacheStore.GetString("DaysLabel")}</span>
                    <span style={{ margin: "0 3px" }}>/</span>
                    <HyperLinkControl
                        text={cacheStore.GetString("QuantityLabel")}
                        id={medicalItemId}
                        target={dosageId}
                        onChange={() => {
                            this.mutateMedicationState(m => {
                                m.SetModeForDosage(dosageId, MedicationDosageDisplayMode.Quantity);
                            }, IsBaseMed);
                        }}
                    />
                </div>
            </div>
        );
    }

    private renderDosageQty(refillQuantity: string, medicalItemId: string, dosageId: string, idy: number, IsNarcotic: boolean, IsBaseMed: boolean): JSX.Element {
        return this.renderRow(
            "QuantityLabel",
            <div className="flex-container meds" style={{ justifyContent: "space-between", marginBottom: "4px" }}>
                <TextboxControl
                    IsSmall
                    IsNumeric
                    className="form-control input"
                    id={"_durationquantity" + medicalItemId + "_" + dosageId}
                    value={refillQuantity}
                    style={{ width: "70px" }}
                    onChange={value =>
                        this.mutateMedicationState(
                            state => {
                                state.mutateDosage(dosageId, p => p.DosageServedQuantity = value);
                                state.RecalculateTotalDuration();
                            },
                            IsBaseMed
                        )
                    }
                />
                <div className="flex-container meds">
                    {!IsNarcotic && (
                        <HyperLinkControl
                            text={cacheStore.GetString("DaysLabel")}
                            id={medicalItemId}
                            target={dosageId}
                            onChange={() =>
                                this.mutateMedicationState(
                                    state => {
                                        state.SetModeForDosage(dosageId, MedicationDosageDisplayMode.Duration);
                                    },
                                    IsBaseMed
                                )
                            }
                        />
                    )}
                    {!IsNarcotic && <span style={{ margin: "0 3px" }}>/</span>}
                    <span>{cacheStore.GetString("QuantityLabel")}</span>
                </div>
            </div>
        );
    }

    public async resolveMedication(medicationResponseLight: MedicationResponse, MedicationId: string, then?: (medication: MedicationResponse) => void): Promise<void> {
        const requestData = {
            CodeGen: medicationResponseLight.CodeGen,
            Form: medicationResponseLight.Form,
            Strength: medicationResponseLight.Strength,
            Name: medicationResponseLight.Name,
            MedicationId: MedicationId
        };

        const response = await Client.postAsync<{ FullMedicationDTO: MedicationResponse }>(
            ServerOperations.Server.MedfarDictionary_ResolveMedicationFromSearch,
            requestData
        );
        if (!response.isSuccess) {
            console.error(response.errors);
            return;
        }

        const medicationDto = response.data.FullMedicationDTO;
        this.mutateState(
            s => {
                if (s.baseMedicationUpdate != null && s.baseMedicationUpdate.Model.Id == medicationDto.Id) {
                    s.baseMedicationUpdate.FromResponseFullUpdate(medicationDto);
                    s.baseMedicationUpdate.SetModeForDosage(s.baseMedicationUpdate.GetLastDosage().Id, MedicationDosageDisplayMode.EndDate);
                }

                if (s.newMedication != null && s.newMedication.Model.Id == medicationDto.Id) {
                    s.newMedication.FromResponseFullUpdate(medicationDto);
                    s.newMedication.SetModeForDosage(s.newMedication.GetLastDosage().Id, MedicationDosageDisplayMode.EndDate);
                }
            },
            () => then?.(medicationDto)
        );
    }

    private save(): void {
        if ((this.state.baseMedicationUpdate != null && this.state.baseMedicationUpdate.Model.Name == null) ||
            (this.state.newMedication != null && this.state.newMedication.Model.Name == null))
            return;

        sessionStore.dispatch(
            LocalPatientOperations.MedicationTab.UpdateMedicationPopup_Save,
            {
                baseMedicationItem: this.state.baseMedicationUpdate,
                newMedicationItem: this.state.newMedication
            }
        );
    }

    private getMedicationNameValidation(isBaseMedication: boolean): void {
        const isValid = isBaseMedication
            ? !IsNullOrWhiteSpace(this.state.baseMedicationUpdate?.Model.Name)
            : !IsNullOrWhiteSpace(this.state.newMedication?.Model.Name);

        this.props.onMedicationNameValidated?.(isValid);
    }

    private onLongTermChange(isChecked: boolean, isBaseMedication: boolean): void {
        if (!cacheStore.ClinicSettings.EnableLongTermMedication)
            return;

        this.mutateState(s => {
            const medicationModel = isBaseMedication ? s.baseMedicationUpdate : s.newMedication;
            medicationModel.Model.IsLongTerm = isChecked;
            s.isLongTermManuallyChanged = true;

            const dosage = medicationModel.GetLastDosage();
            if (
                !settingsStore.configuration.get("LongTermMedicationDurationInDays")
                || dosage.DisplayMode === MedicationDosageDisplayMode.Quantity
                || (s.isEndDateManuallyChanged && dosage.DisplayMode === MedicationDosageDisplayMode.EndDate)
                || medicationModel.IsNarcotic()
                || medicationModel.Model.IsShortTerm
            ) {
                return;
            }

            // Automatically update duration and end date
            medicationModel.Model.Dosages?.forEach(dosage => {
                if (s.isDurationManuallyChangedByDosageId[dosage.Id])
                    return;

                dosage.Duration = isChecked
                    ? medicationModel.DaysToTicks(settingsStore.configuration.get("LongTermMedicationDurationInDays"))
                    : s.initialDurationByDosageId[dosage.Id];
            });

            if (medicationModel.Model.RefillStartDate) {
                const initialEndDateTmp = this.getCurrentStateManager(s).isCease()
                    ? this.props.baseMedication.Model.CeaseDate
                    : this.props.baseMedication.Model.RefillEndDate;
                const initialEndDate = initialEndDateTmp
                    ? new Date(initialEndDateTmp) // in case the date is actually a string
                    : null;
                const newEndDate = isChecked
                    ? moment(medicationModel.Model.RefillStartDate).add(medicationModel.TicksToDays(dosage.Duration, false), "days").toDate()
                    : initialEndDate;

                if (this.getCurrentStateManager(s).isCease())
                    medicationModel.Model.CeaseDate = newEndDate;
                else
                    medicationModel.Model.RefillEndDate = newEndDate;
            }

            medicationModel.RecalculateTotalDuration();
        });
    }

    private onDurationChange(value: string, dosageId: string, isBaseMedication: boolean): void {
        this.mutateState(s => {
            const medicationModel = isBaseMedication ? s.baseMedicationUpdate : s.newMedication;
            medicationModel.mutateDosage(dosageId, d => d.Duration = medicationModel.DaysToTicks(value));
            medicationModel.RecalculateTotalDuration();
            s.isDurationManuallyChangedByDosageId[dosageId] = true;

            if (
                !cacheStore.ClinicSettings.EnableLongTermMedication
                || s.isLongTermManuallyChanged
                || !settingsStore.configuration.get("LongTermMedicationDurationInDays")
                || medicationModel.Model.IsShortTerm
            )
                return;

            medicationModel.Model.IsLongTerm =
                Number(value) === settingsStore.configuration.get("LongTermMedicationDurationInDays")
                || this.props.baseMedication.Model.IsLongTerm;
        });
    }

    private onStartDateChange(medicationModel: MedicationModel, newDate: Date, isEndDateManuallyChanged: boolean, isCease: boolean): void {
        if (
            !cacheStore.ClinicSettings.EnableLongTermMedication
            || !medicationModel.Model.IsLongTerm
            || medicationModel.Model.IsShortTerm
            || isEndDateManuallyChanged
            || !settingsStore.configuration.get("LongTermMedicationDurationInDays")
        ) {
            return;
        }

        const start = DateOnly.parseUnlessNull(newDate)?.toJSDate();
        if (start) {
            const end = moment(start).add(settingsStore.configuration.get("LongTermMedicationDurationInDays"), "days").toDate();
            if (isCease)
                medicationModel.Model.CeaseDate = end;
            else
                medicationModel.Model.RefillEndDate = end;
        }
        else { // restore initial end date
            if (isCease)
                medicationModel.Model.CeaseDate = this.props.baseMedication.Model.CeaseDate;
            else
                medicationModel.Model.RefillEndDate = this.props.baseMedication.Model.RefillEndDate;
        }
    }

    private onDateChange(dateWithPrecision: DateTimeWithPrecision, dateTextInput: string): DateChangeResult {
        const precision = dateWithPrecision.precision ?? LifeStageDatePrecisionEnum.Day;
        let date = null;
        let datePrecision = null;
        let dateText = dateTextInput;

        if (dateWithPrecision.value) {
            date = moment(dateWithPrecision.value).toDate();

            if (precision == LifeStageDatePrecisionEnum.DayApprox || precision == LifeStageDatePrecisionEnum.Day) {
                datePrecision = LifeStageDatePrecisionEnum.Day;
                dateText = moment(dateWithPrecision.value).format("YYYY-MM-DD");
            }
            else {
                datePrecision = precision;
                dateText = PatientMedicalItemsHelper.GetLifeStageDateString(dateWithPrecision.value, precision);
            }
        }
        if (IsNullOrWhiteSpace(dateWithPrecision.text)) {
            date = null;
            dateText = "";
        }

        return { date, datePrecision, dateText };
    }
}

export { PatientMedicationView, PatientMedicationViewState };
